'use client';

import React, { useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { tagsForTarget } from './ontology/tags';
import type { FeedbackSubmission } from './ontology/types';
import { submitFeedback } from './submit';

/** Describes the dialog state and captured snapshot consumed by FeedbackForm. */
interface FeedbackFormProps {
  isOpen: boolean;
  snapshot: Omit<FeedbackSubmission, 'tags' | 'unselected_tags' | 'message'>;
  onClose: () => void;
}

/** Collects feedback tags and details, then submits a normalized feedback record. */
export const FeedbackForm: React.FC<FeedbackFormProps> = ({ isOpen, snapshot, onClose }) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const offeredTags: string[] = useMemo(() => {
    return tagsForTarget(snapshot.target);
  }, [snapshot.target]);

  const canSubmit: boolean = selectedTags.length >= 1 || message.trim() !== '';

  /** Toggles whether a tag is included in the pending feedback submission. */
  const toggleTag = (tag: string) => {
    setSelectedTags((currentTags) => {
      return currentTags.includes(tag)
        ? currentTags.filter((currentTag) => currentTag !== tag)
        : [...currentTags, tag];
    });
  };

  /** Submits the current feedback record when the user has provided input. */
  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit || isSubmitting) {
      return;
    }

    const selectedTagSet = new Set(selectedTags);
    const record: FeedbackSubmission = {
      ...snapshot,
      tags: selectedTags,
      unselected_tags: offeredTags.filter((tag) => !selectedTagSet.has(tag)),
      message: message.trim(),
    };

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitFeedback(record);
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError('Could not submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isSubmitting ? undefined : onClose}
      disableEscapeKeyDown={isSubmitting}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Give feedback</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Select any tags that fit, then add optional details.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {offeredTags.map((tag: string) => {
                const isSelected: boolean = selectedTags.includes(tag);

                return (
                  <Chip
                    key={tag}
                    label={tag}
                    clickable
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => toggleTag(tag)}
                    sx={{
                      borderColor: isSelected
                        ? 'var(--mui-palette-primary-main)'
                        : 'var(--mui-palette-divider)',
                      color: isSelected
                        ? 'var(--mui-palette-primary-contrastText)'
                        : 'var(--mui-palette-text-primary)',
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
          <TextField
            label="Tell us more"
            value={message}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setMessage(event.target.value)
            }
            multiline
            minRows={4}
            fullWidth
          />
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose} variant="text" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit || isSubmitting}
          variant="contained"
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
